import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate, useParams } from 'react-router';

import {
  TranscriptorApiError,
  transcriptorApi,
  TranscriptorEncounterResponse,
  UsedEncounterSchema,
} from '@/shared/api/transcriptor/transcriptorApi';
import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { toastCaller } from '@/shared/ui';

import { encounterModel } from '@/entities/encounter';
import { organizationModel } from '@/entities/organization';

import { getCallerName } from '../lib/getCallerName';
import {
  EncounterGroup,
  groupEncountersByDay,
} from '../lib/groupEncountersByDay';

const PAGE_SIZE = 50;
const SCROLL_PRELOAD_MARGIN = '300px';

export type EncountersListState =
  | { kind: 'empty' }
  | { kind: 'error'; message: string }
  | { kind: 'loading' }
  | { kind: 'no-organization' }
  | { kind: 'ready' };

export type ApproveDocumentArgs = {
  documentId: string;
  approved: boolean;
};

export type AttachPatientArgs = {
  patientId: string;
};

export type UseEncountersPageResult = {
  error: string | null;
  fetchedEncounterError: string | null;
  fetchedEncounterLoading: boolean;
  groups: EncounterGroup[];
  hasMore: boolean;
  isMobile: boolean;
  listState: EncountersListState;
  loadingMore: boolean;
  scrollContainerRef: RefObject<HTMLDivElement>;
  searchQuery: string;
  selectedEncounter: TranscriptorEncounterResponse | null;
  selectedEncounterId: string | undefined;
  selectedSchemaCode: string | null;
  sentinelRef: RefObject<HTMLDivElement>;
  setSearchQuery: (value: string) => void;
  setSelectedSchemaCode: (value: string | null) => void;
  clearSelectedEncounter: () => void;
  selectEncounter: (encounterId: string) => void;
  approveDocument: (args: ApproveDocumentArgs) => Promise<void>;
  attachPatient: (args: AttachPatientArgs) => Promise<void>;
  usedSchemas: UsedEncounterSchema[];
};

const getErrorMessage = (error: unknown) =>
  (error instanceof TranscriptorApiError || error instanceof Error) &&
  error.message
    ? error.message
    : 'Unknown error';

export const useEncountersPage = (): UseEncountersPageResult => {
  const { isMobile } = useMedia();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { encounterId } = useParams<{ encounterId: string }>();
  const selectedEncounterId = encounterId;
  const currentOrganizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const encountersById = useAppSelector(
    encounterModel.selectors.selectEntities,
  );
  const selectedEncounter =
    useAppSelector(
      encounterModel.selectors.selectEncounterById(selectedEncounterId),
    ) ?? null;

  const [encounterIds, setEncounterIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSchemaCode, setSelectedSchemaCode] = useState<string | null>(
    null,
  );
  const [usedSchemas, setUsedSchemas] = useState<UsedEncounterSchema[]>([]);
  const [fetchedEncounterLoading, setFetchedEncounterLoading] = useState(false);
  const [fetchedEncounterError, setFetchedEncounterError] = useState<
    string | null
  >(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const selectEncounter = useCallback(
    (id: string) => {
      navigate(generatePath(PATHS.encounter, { encounterId: id }));
    },
    [navigate],
  );

  const clearSelectedEncounter = useCallback(() => {
    navigate(PATHS.encounters);
  }, [navigate]);

  const encounters = useMemo(
    () =>
      encounterIds
        .map((id) => encountersById[id])
        .filter(
          (encounter): encounter is TranscriptorEncounterResponse =>
            encounter !== undefined,
        ),
    [encounterIds, encountersById],
  );

  const visibleEncounters = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();

    if (!trimmed) {
      return encounters;
    }

    return encounters.filter((encounter) =>
      getCallerName(encounter).toLowerCase().includes(trimmed),
    );
  }, [encounters, searchQuery]);

  const groups = useMemo(
    () => groupEncountersByDay(visibleEncounters),
    [visibleEncounters],
  );

  const listState = useMemo<EncountersListState>(() => {
    if (!currentOrganizationID) {
      return { kind: 'no-organization' };
    }

    if (loading) {
      return { kind: 'loading' };
    }

    if (error) {
      return { kind: 'error', message: error };
    }

    if (visibleEncounters.length === 0) {
      return { kind: 'empty' };
    }

    return { kind: 'ready' };
  }, [currentOrganizationID, error, loading, visibleEncounters.length]);

  useEffect(() => {
    if (!currentOrganizationID) {
      setEncounterIds([]);
      setPage(0);
      setHasMore(false);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);
    setEncounterIds([]);
    setPage(0);
    setHasMore(false);

    dispatch(
      encounterModel.thunks.fetchEncounterList({
        organizationId: currentOrganizationID,
        page: 1,
        limit: PAGE_SIZE,
        schemaCode: selectedSchemaCode,
      }),
    )
      .unwrap()
      .then((result) => {
        if (cancelled) {
          return;
        }

        setEncounterIds(result.ids);
        setPage(result.page);
        setHasMore(PAGE_SIZE < result.total);
      })
      .catch((caughtError) => {
        if (cancelled) {
          return;
        }

        setError(getErrorMessage(caughtError));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentOrganizationID, selectedSchemaCode]);

  const loadMore = useCallback(async () => {
    if (!currentOrganizationID || loading || loadingMore || !hasMore) {
      return;
    }

    setLoadingMore(true);

    try {
      const nextPage = page + 1;
      const result = await dispatch(
        encounterModel.thunks.fetchEncounterList({
          organizationId: currentOrganizationID,
          page: nextPage,
          limit: PAGE_SIZE,
          schemaCode: selectedSchemaCode,
        }),
      ).unwrap();

      setEncounterIds((previousIds) => [...previousIds, ...result.ids]);
      setPage(nextPage);
      setHasMore(nextPage * PAGE_SIZE < result.total);
    } catch (caughtError) {
      setError(getErrorMessage(caughtError));
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [
    currentOrganizationID,
    hasMore,
    loading,
    loadingMore,
    page,
    selectedSchemaCode,
  ]);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: SCROLL_PRELOAD_MARGIN,
      },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMore]);

  useEffect(() => {
    if (!selectedEncounterId) {
      setFetchedEncounterError(null);
      setFetchedEncounterLoading(false);
      return;
    }

    let cancelled = false;

    setFetchedEncounterLoading(true);
    setFetchedEncounterError(null);

    dispatch(encounterModel.thunks.fetchEncounter(selectedEncounterId))
      .unwrap()
      .catch((caughtError) => {
        if (cancelled) {
          return;
        }

        setFetchedEncounterError(getErrorMessage(caughtError));
      })
      .finally(() => {
        if (!cancelled) {
          setFetchedEncounterLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedEncounterId]);

  useEffect(() => {
    if (!currentOrganizationID) {
      setUsedSchemas([]);
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      try {
        const schemas = await transcriptorApi.getUsedEncounterSchemas({
          organization_id: currentOrganizationID,
        });

        if (!controller.signal.aborted) {
          setUsedSchemas(schemas);
        }
      } catch {
        if (!controller.signal.aborted) {
          setUsedSchemas([]);
        }
      }
    };

    load();

    return () => {
      controller.abort();
    };
  }, [currentOrganizationID]);

  const attachPatient = useCallback(
    async ({ patientId }: AttachPatientArgs) => {
      if (!selectedEncounterId) {
        return;
      }

      try {
        await dispatch(
          encounterModel.thunks.attachPatient({
            encounterId: selectedEncounterId,
            patientId,
          }),
        ).unwrap();
      } catch (caughtError) {
        toastCaller({
          type: 'error',
          message: formatMessage(
            {
              id: 'encounters.details.attachPatientError',
              defaultMessage: 'Failed to attach patient: {message}',
            },
            { message: getErrorMessage(caughtError) },
          ),
        });
        throw caughtError;
      }
    },
    [selectedEncounterId, formatMessage],
  );

  const approveDocument = useCallback(
    async ({ documentId, approved }: ApproveDocumentArgs) => {
      if (!selectedEncounterId) {
        return;
      }

      try {
        await dispatch(
          encounterModel.thunks.approveDocument({
            encounterId: selectedEncounterId,
            documentId,
            approved,
          }),
        ).unwrap();
      } catch (caughtError) {
        toastCaller({
          type: 'error',
          message: formatMessage(
            {
              id: 'encounters.details.approveError',
              defaultMessage: 'Failed to update approval: {message}',
            },
            { message: getErrorMessage(caughtError) },
          ),
        });
      }
    },
    [selectedEncounterId, formatMessage],
  );

  return {
    error,
    fetchedEncounterError,
    fetchedEncounterLoading,
    groups,
    hasMore,
    isMobile,
    listState,
    loadingMore,
    scrollContainerRef,
    searchQuery,
    selectedEncounter,
    selectedEncounterId,
    selectedSchemaCode,
    sentinelRef,
    setSearchQuery,
    setSelectedSchemaCode,
    clearSelectedEncounter,
    selectEncounter,
    approveDocument,
    attachPatient,
    usedSchemas,
  };
};
